import { isMobile } from 'react-device-detect';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  GET_PERFORMER_FAN_CLUB_INFO,
  GET_PERFORMER_FAN_CLUB_INFO_ERROR,
  GET_PERFORMER_FAN_CLUB_INFO_RESPONSE,
} from 'services/MessagingService/EventNames';
import routeDefinitions from 'enum/routeDefinitions';
import { isValidResponse, getDomain } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const EPAY_SERVER = ConfigurationService.get(ConfigurationKeys.EPAY_SERVER);
const EPAY_TENANT = ConfigurationService.get(ConfigurationKeys.EPAY_TENANT);
const EPAY_API = `${EPAY_SERVER}/rest/v1/checkout/${EPAY_TENANT}`;

const FANCLUB_SUBSCRIPTIONS_URL = `${BASE_URL}/rest/v1.0/profile/fanclub/{username}/subscriptions`;
const LIBRARY_VIDEOS_URL = `${BASE_URL}/rest/v1.0/marketplace/libraries/videos/{username}`;
const GET_FANCLUB_INFO_URL = `${BASE_URL}/rest/v1.0/profile/{username}/fanclub`;
// eslint-disable-next-line max-len
const FAN_CLUB_PRODUCT_URL = `${EPAY_API}/en/FANCLUB?userName={username}&accessHash={accessHash}&target={performer}&amount={amount}`;
const PAY_SUBSCRIPTION_URL = `${EPAY_API}/en/{username}/{productId}/{processorId}`;
const GET_FANCLUB_AUTHORIZATION_URL = `${BASE_URL}/rest/v1.0/profile/{username}/fanclub/authorization`;

class FanClubService {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(GET_PERFORMER_FAN_CLUB_INFO, this.getPerformerFanClubInfo);
  }

  destructor() {
    this.messagingService.off(GET_PERFORMER_FAN_CLUB_INFO, this.getPerformerFanClubInfo);
  }

  getUserFanclubs = (username, accessHash) => {
    const rawUrl = ApiService.substitute(FANCLUB_SUBSCRIPTIONS_URL, { username: encodeURIComponent(username) });
    const encodedUrl = encodeURI(rawUrl);
    const queryParams = {
      'login_user_name': username,
      accessHash,
      offset: 0,
      limit: 20,
    };

    return ApiService.get(encodedUrl, { params: queryParams }).then((res) => res.data);
  };

  getLibraryVideos(username) {
    const queryParams = { sortBy: 'date' };
    const rawUrl = ApiService.substitute(LIBRARY_VIDEOS_URL, { username: encodeURIComponent(username) });
    const encodedUrl = encodeURI(rawUrl);

    return ApiService.get(encodedUrl, { params: queryParams }).then((res) => res.data);
  }

  getPerformerFanClubInfo = (username) => {
    const rawUrl = ApiService.substitute(GET_FANCLUB_INFO_URL, { username });
    const encodedUrl = encodeURI(rawUrl);

    return ApiService.get(encodedUrl)
      .then((res) => {
        if (res.data) {
          this.messagingService.emit(GET_PERFORMER_FAN_CLUB_INFO_RESPONSE, res.data);
        }
      })
      .catch((err) => this.messagingService.emit(GET_PERFORMER_FAN_CLUB_INFO_ERROR, err));
  };

  subscribeToFanClub = async(username, accessHash, performerName, tokenPrice) => {
    let result = undefined;
    try {
      const fanClubProductResponse = await this.getFanClubProduct(username, accessHash, performerName, tokenPrice);
      if (!isValidResponse(fanClubProductResponse)) {
        return result;
      }
      const rawUrl = ApiService.substitute(PAY_SUBSCRIPTION_URL, {
        username,
        productId: fanClubProductResponse.data.products[0].id,
        processorId: fanClubProductResponse.data.products[0].categories[0].methods[0].processorId,
      });
      const encodedUrl = encodeURI(rawUrl);
      const postParams = {
        userName: username,
        accessHash,
        target: performerName,
        amount: parseInt(tokenPrice, 10),
        originalDomain: getDomain(),
        returnPath: routeDefinitions.checkout,
        device: isMobile ? 'Mobile' : 'Desktop',
      };

      result = await ApiService.post(encodedUrl, { data: postParams, withCredentials: false });
    } catch (error) {
      parseCatch(error);
    }

    return result;
  };

  getFanClubProduct(username, accessHash, performer, tokenPrice) {
    const rawUrl = ApiService.substitute(FAN_CLUB_PRODUCT_URL, { username, accessHash, performer, amount: tokenPrice });
    const encodedUrl = encodeURI(rawUrl);

    return ApiService.get(encodedUrl, { withCredentials: false });
  }

  getPerformerFanClubAuth(username) {
    const rawUrl = ApiService.substitute(GET_FANCLUB_AUTHORIZATION_URL, { username });
    const encodedUrl = encodeURI(rawUrl);

    return ApiService.get(encodedUrl);
  }
}

export default new FanClubService();
